import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Card,
  Stack,
  Text,
  Group,
  Button,
  ActionIcon,
  SimpleGrid,
  Alert,
  Progress,
  Badge,
  Modal,
  Textarea,
  TextInput,
  Loader,
  Center,
  Tooltip,
  Tabs,
  Divider,
  Checkbox,
  Input,
} from '@mantine/core';
import {
  IconUpload,
  IconTrash,
  IconEdit,
  IconX,
  IconPlus,
  IconExclamationCircle,
  IconPhoto,
  IconVideo,
  IconChevronUp,
  IconChevronDown,
  IconCheck,
  IconLock,
  IconEye,
  IconInfoCircle,
  IconClock,
  IconClockCheck,
} from '@tabler/icons-react';
import { useApiClient } from '../../api';
import { uploadSellerMedia } from '../../api/uploads';
import ProtectedMediaItem from '../media/ProtectedMediaItem';

// Public gallery limits
const PUBLIC_MAX_PHOTOS = 15;
const PUBLIC_MAX_VIDEOS = 3;
const PUBLIC_MAX_VIDEO_SECONDS = 10;

// Private gallery limits
const PRIVATE_MAX_VIDEO_SECONDS = 300; // 5 minutes

// The media list endpoint is paginated (max limit 100). The public gallery is
// bounded (15 photos + 3 videos), but the private gallery is unlimited, so
// pages are fetched incrementally and appended ("Load more" while
// total > loaded — the SellerAlbums gallery-picker pattern).
const MEDIA_PAGE_LIMIT = 100;

// Dedupe by id in case items shifted between page fetches
function dedupeById(items) {
  const seen = new Set();
  return items.filter((item) => (seen.has(item.id) ? false : (seen.add(item.id), true)));
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// Scheduling helpers (contract §5: publish_at — future ISO hides the item from
// the public gallery until due; null publishes immediately)

function isScheduled(item) {
  return !!item.publish_at && new Date(item.publish_at) > new Date();
}

function formatShortDateTime(iso) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// ISO timestamp → value for <input type="datetime-local"> (local time, minutes)
function toLocalInputValue(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function isFutureLocalValue(value) {
  if (!value) return false;
  const d = new Date(value);
  return !Number.isNaN(d.getTime()) && d > new Date();
}

// Check video duration before adding to queue
function getVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => reject(new Error('Could not read video file'));
    video.src = URL.createObjectURL(file);
  });
}

// ── Edit modal ───────────────────────────────────────────────────────────────

function EditMediaModal({ item, opened, onClose, onSave }) {
  const scheduled = item ? isScheduled(item) : false;
  const [title, setTitle] = useState(item?.title || '');
  const [caption, setCaption] = useState(item?.caption || '');
  const [publishAt, setPublishAt] = useState(scheduled ? toLocalInputValue(item.publish_at) : '');
  const [saving, setSaving] = useState(false);

  // Only scheduled items expose the reschedule picker; the new value must stay
  // in the future (the API 422s otherwise). "Publish now" lives on the card.
  const publishAtInvalid = scheduled && !isFutureLocalValue(publishAt);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { title, caption };
      if (scheduled) data.publish_at = new Date(publishAt).toISOString();
      await onSave(item.id, data);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Edit Media Info" size="md">
      <Stack gap="md">
        <TextInput label="Title" placeholder="Optional title" value={title}
          onChange={(e) => setTitle(e.currentTarget.value)} maxLength={200} />
        <Textarea label="Caption" placeholder="Optional caption" value={caption}
          onChange={(e) => setCaption(e.currentTarget.value)} maxLength={500} autosize minRows={2} />
        {scheduled && (
          <Input.Wrapper label="Scheduled for" description="Publishes to your gallery at this time"
            error={publishAtInvalid ? 'Pick a date and time in the future' : null}>
            <Input type="datetime-local" value={publishAt} min={toLocalInputValue(new Date().toISOString())}
              onChange={(e) => setPublishAt(e.currentTarget.value)} aria-label="Scheduled publish time" />
          </Input.Wrapper>
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button color="grape" onClick={handleSave} loading={saving} disabled={publishAtInvalid}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Media card ───────────────────────────────────────────────────────────────

function MediaCard({ item, onDelete, onEdit, onMoveUp, onMoveDown, onToggleVisibility, onPublishNow, isFirst, isLast }) {
  const scheduled = isScheduled(item);
  // Private or scheduled-future files 404 on public /media (contract §5) — fetch
  // them through the authenticated seller-media file route as an object URL.
  // Rendering (image-eager / video-on-play) lives in ProtectedMediaItem.
  const isProtected = !item.is_public || scheduled;
  return (
    <Card shadow="xs" padding="xs" radius="md" withBorder>
      <Stack gap="xs">
        <div style={{ position: 'relative', height: 130, borderRadius: 6, overflow: 'hidden', background: '#111' }}>
          <ProtectedMediaItem
            mediaId={item.id}
            mediaType={item.media_type}
            title={item.title}
            alt={item.title || 'Media'}
            filePath={item.file_path}
            isProtected={isProtected}
          />
          <Badge size="xs" variant="filled" color={item.media_type === 'video' ? 'blue' : 'grape'}
            style={{ position: 'absolute', top: 4, left: 4 }}>
            {item.media_type === 'video' ? <IconVideo size="0.7rem" /> : <IconPhoto size="0.7rem" />}
          </Badge>
        </div>
        <Text size="xs" c="dimmed" lineClamp={1}>{item.title || item.original_filename || 'Untitled'}</Text>
        {item.file_size ? <Text size="xs" c="dimmed">{formatBytes(item.file_size)}</Text> : null}
        {scheduled && (
          <Badge size="xs" variant="light" color="orange" leftSection={<IconClock size="0.7rem" />}>
            Scheduled · {formatShortDateTime(item.publish_at)}
          </Badge>
        )}
        <Group gap="xs" justify="space-between">
          <Group gap={4}>
            <Tooltip label="Move up">
              <ActionIcon size="xs" variant="subtle" onClick={onMoveUp} disabled={isFirst}><IconChevronUp size="0.8rem" /></ActionIcon>
            </Tooltip>
            <Tooltip label="Move down">
              <ActionIcon size="xs" variant="subtle" onClick={onMoveDown} disabled={isLast}><IconChevronDown size="0.8rem" /></ActionIcon>
            </Tooltip>
          </Group>
          <Group gap={4}>
            {scheduled && (
              <Tooltip label="Publish now">
                <ActionIcon size="xs" variant="subtle" color="green" aria-label="Publish now" onClick={onPublishNow}>
                  <IconClockCheck size="0.8rem" />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label={item.is_public ? 'Make private' : 'Make public'}>
              <ActionIcon size="xs" variant="subtle" color={item.is_public ? 'grape' : 'blue'} onClick={onToggleVisibility}>
                {item.is_public ? <IconLock size="0.8rem" /> : <IconEye size="0.8rem" />}
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Edit info">
              <ActionIcon size="xs" variant="subtle" color="blue" onClick={onEdit}><IconEdit size="0.8rem" /></ActionIcon>
            </Tooltip>
            <Tooltip label="Delete">
              <ActionIcon size="xs" variant="subtle" color="red" onClick={onDelete}><IconTrash size="0.8rem" /></ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Stack>
    </Card>
  );
}

// ── Queue item ───────────────────────────────────────────────────────────────

function QueueItem({ item, onRemove }) {
  const previewUrl = URL.createObjectURL(item.file);
  const isVideo = item.file.type.startsWith('video/');
  return (
    <Card shadow="xs" padding="xs" radius="md" withBorder style={{ opacity: item.uploading ? 0.7 : 1 }}>
      <Stack gap="xs">
        <div style={{ position: 'relative', height: 90, borderRadius: 6, overflow: 'hidden', background: '#111' }}>
          {isVideo
            ? <video src={previewUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <img src={previewUrl} alt={item.file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
          {item.done && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,150,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconCheck size="2rem" color="white" />
            </div>
          )}
          {!item.uploading && !item.done && (
            <ActionIcon size="sm" variant="filled" color="red" style={{ position: 'absolute', top: 4, right: 4 }} onClick={() => onRemove(item.id)}>
              <IconX size="0.8rem" />
            </ActionIcon>
          )}
        </div>
        <Text size="xs" c="dimmed" lineClamp={1}>{item.file.name}</Text>
        {item.error && <Text size="xs" c="red" lineClamp={2}>{item.error}</Text>}
        {item.uploading && <Progress value={100} animated color="grape" size="xs" />}
      </Stack>
    </Card>
  );
}

// ── Upload panel (reused for both tabs) ─────────────────────────────────────

function UploadPanel({
  isPublic,
  media,
  queue,
  uploading,
  globalError,
  onFileSelect,
  onUploadAll,
  onClearQueue,
  onRemoveFromQueue,
  onDelete,
  onEdit,
  onMoveUp,
  onMoveDown,
  onToggleVisibility,
  onPublishNow,
  editItem,
  onCloseEdit,
  onSaveEdit,
}) {
  const fileInputRef = useRef(null);

  // Optional scheduling for the queued batch (contract §5 publish_at)
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleValue, setScheduleValue] = useState('');
  const scheduleInvalid = scheduleEnabled && !isFutureLocalValue(scheduleValue);

  const handleUploadClick = async () => {
    const publishAt = scheduleEnabled ? new Date(scheduleValue).toISOString() : null;
    await onUploadAll(publishAt);
    setScheduleEnabled(false);
    setScheduleValue('');
  };

  const photos = media.filter(m => m.media_type === 'image');
  const videos = media.filter(m => m.media_type === 'video');

  // Remaining capacity for public gallery
  const photosLeft = isPublic ? PUBLIC_MAX_PHOTOS - photos.length : Infinity;
  const videosLeft = isPublic ? PUBLIC_MAX_VIDEOS - videos.length : Infinity;
  const isFull = isPublic && photosLeft <= 0 && videosLeft <= 0;

  return (
    <Stack gap="lg">
      {/* Info banner */}
      {isPublic ? (
        <Alert icon={<IconEye size="1rem" />} color="blue" variant="light">
          <Text size="sm" fw={500}>Visible to all users</Text>
          <Text size="xs" c="dimmed" mt={2}>
            All content in your public gallery can be seen by everyone on PediPiggy — no account required.
            Upload your best work to attract new buyers!
          </Text>
        </Alert>
      ) : (
        <Alert icon={<IconLock size="1rem" />} color="grape" variant="light">
          <Text size="sm" fw={500}>Private gallery — unlocked after pedicure purchase</Text>
          <Text size="xs" c="dimmed" mt={2}>
            Your private gallery is blurred for all users until they spoil you with a pedicure.
            Once a buyer completes a purchase, your private gallery is fully unlocked for them.
            You can post unlimited photos and videos up to 5 minutes long here.
          </Text>
        </Alert>
      )}

      {/* Limits summary (public only) */}
      {isPublic && (
        <Group gap="xl">
          <Stack gap={2}>
            <Text size="xs" c="dimmed">Photos</Text>
            <Group gap={4}>
              <Text fw={600} size="sm" c={photos.length >= PUBLIC_MAX_PHOTOS ? 'red' : 'dark'}>
                {photos.length}/{PUBLIC_MAX_PHOTOS}
              </Text>
              {photos.length >= PUBLIC_MAX_PHOTOS && <Badge size="xs" color="red">Full</Badge>}
            </Group>
          </Stack>
          <Stack gap={2}>
            <Text size="xs" c="dimmed">Videos (10 sec max each)</Text>
            <Group gap={4}>
              <Text fw={600} size="sm" c={videos.length >= PUBLIC_MAX_VIDEOS ? 'red' : 'dark'}>
                {videos.length}/{PUBLIC_MAX_VIDEOS}
              </Text>
              {videos.length >= PUBLIC_MAX_VIDEOS && <Badge size="xs" color="red">Full</Badge>}
            </Group>
          </Stack>
        </Group>
      )}

      {/* Error */}
      {globalError && (
        <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
          <Text style={{ whiteSpace: 'pre-line' }}>{globalError}</Text>
        </Alert>
      )}

      {/* Queue */}
      {queue.length > 0 && (
        <SimpleGrid cols={{ base: 3, sm: 4, md: 5 }} spacing="sm">
          {queue.map(item => (
            <QueueItem key={item.id} item={item} onRemove={onRemoveFromQueue} />
          ))}
        </SimpleGrid>
      )}

      {/* Upload buttons */}
      {!isFull && (
        <Group gap="sm" wrap="wrap">
          <Button leftSection={<IconPlus size="1rem" />} variant="outline" color="grape"
            onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {isPublic ? 'Select Files' : 'Add to Private Gallery'}
          </Button>
          {queue.length > 0 && (
            <>
              <Button leftSection={<IconUpload size="1rem" />} color="grape"
                onClick={handleUploadClick} loading={uploading} disabled={scheduleInvalid}>
                Upload {queue.length} file{queue.length !== 1 ? 's' : ''}
              </Button>
              <Button variant="subtle" color="red" onClick={onClearQueue} disabled={uploading}>
                Clear
              </Button>
            </>
          )}
          <input
            ref={fileInputRef} type="file" multiple
            accept="image/*,video/*"
            onChange={(e) => onFileSelect(e.target.files)}
            style={{ display: 'none' }}
          />
        </Group>
      )}

      {/* Optional scheduling for the queued batch */}
      {!isFull && queue.length > 0 && (
        <Stack gap="xs">
          <Checkbox
            label="Schedule for later"
            description="Uploads stay hidden from your gallery until the scheduled time"
            color="grape"
            checked={scheduleEnabled}
            onChange={(e) => setScheduleEnabled(e.currentTarget.checked)}
            disabled={uploading}
          />
          {scheduleEnabled && (
            <Input.Wrapper label="Publish at" maw={280}
              error={scheduleInvalid ? 'Pick a date and time in the future' : null}>
              <Input
                type="datetime-local"
                value={scheduleValue}
                min={toLocalInputValue(new Date().toISOString())}
                onChange={(e) => setScheduleValue(e.currentTarget.value)}
                disabled={uploading}
                aria-label="Publish at"
              />
            </Input.Wrapper>
          )}
        </Stack>
      )}

      {isFull && (
        <Alert color="orange" variant="light" icon={<IconInfoCircle size="1rem" />}>
          Your public gallery is full (15 photos + 3 videos). Delete an item to add more.
        </Alert>
      )}

      <Divider />

      {/* Gallery grid */}
      {media.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="sm">
            {isPublic ? <IconPhoto size="3rem" color="var(--mantine-color-gray-4)" /> : <IconLock size="3rem" color="var(--mantine-color-gray-4)" />}
            <Text c="dimmed">{isPublic ? 'No public media yet — add some to attract buyers!' : 'Your private gallery is empty'}</Text>
          </Stack>
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
          {media.map((item, index) => (
            <MediaCard
              key={item.id} item={item}
              isFirst={index === 0} isLast={index === media.length - 1}
              onDelete={() => onDelete(item.id)}
              onEdit={() => onEdit(item)}
              onMoveUp={() => onMoveUp(index)}
              onMoveDown={() => onMoveDown(index)}
              onToggleVisibility={() => onToggleVisibility(item)}
              onPublishNow={() => onPublishNow(item)}
            />
          ))}
        </SimpleGrid>
      )}

      {editItem && (
        <EditMediaModal
          key={editItem.id} item={editItem} opened={!!editItem}
          onClose={onCloseEdit} onSave={onSaveEdit}
        />
      )}
    </Stack>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const SellerMediaGallery = () => {
  const api = useApiClient();

  // Incremental page accumulation: `pagesLoadedRef` tracks how deep the user
  // has paged; refetch (after every mutation) reloads that same depth so
  // loaded items never silently disappear.
  const [mediaState, setMediaState] = useState({ items: [], total: null });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const pagesLoadedRef = useRef(1);

  const refetch = useCallback(async () => {
    try {
      const count = Math.max(1, pagesLoadedRef.current);
      let items = [];
      let total = 0;
      for (let p = 1; p <= count; p += 1) {
        const res = await api.get('/api/v1/me/seller/media', { page: p, limit: MEDIA_PAGE_LIMIT });
        items = items.concat(res.items);
        total = res.total;
        if (items.length >= total) {
          pagesLoadedRef.current = p;
          break;
        }
      }
      setMediaState({ items: dedupeById(items), total });
      setLoadError(null);
    } catch (err) {
      setLoadError(err);
    }
  }, [api]);

  useEffect(() => {
    let active = true;
    (async () => {
      await refetch();
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [refetch]);

  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const next = pagesLoadedRef.current + 1;
      const res = await api.get('/api/v1/me/seller/media', { page: next, limit: MEDIA_PAGE_LIMIT });
      pagesLoadedRef.current = next;
      setMediaState((prev) => ({
        items: dedupeById([...prev.items, ...res.items]),
        total: res.total,
      }));
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const [publicQueue, setPublicQueue] = useState([]);
  const [privateQueue, setPrivateQueue] = useState([]);
  const [publicUploading, setPublicUploading] = useState(false);
  const [privateUploading, setPrivateUploading] = useState(false);
  const [publicError, setPublicError] = useState(null);
  const [privateError, setPrivateError] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const allMedia = mediaState.items;
  const hasMore = mediaState.total != null && allMedia.length < mediaState.total;
  const publicMedia = allMedia.filter(m => m.is_public);
  const privateMedia = allMedia.filter(m => !m.is_public);

  const publicPhotos = publicMedia.filter(m => m.media_type === 'image');
  const publicVideos = publicMedia.filter(m => m.media_type === 'video');

  // ── File selection with limit/duration checks ──────────────────────────

  const handleFileSelect = async (files, isPublic) => {
    if (!files || files.length === 0) return;
    const setError = isPublic ? setPublicError : setPrivateError;
    const setQueue = isPublic ? setPublicQueue : setPrivateQueue;
    setError(null);

    const accepted = [];
    const errors = [];

    for (const file of Array.from(files)) {
      const isImg = file.type.startsWith('image/');
      const isVid = file.type.startsWith('video/');

      if (!isImg && !isVid) {
        errors.push(`${file.name}: only images and videos are allowed`);
        continue;
      }

      // Public gallery capacity checks
      if (isPublic) {
        if (isImg && publicPhotos.length + accepted.filter(a => a.file.type.startsWith('image/')).length >= PUBLIC_MAX_PHOTOS) {
          errors.push(`${file.name}: public gallery is limited to ${PUBLIC_MAX_PHOTOS} photos`);
          continue;
        }
        if (isVid && publicVideos.length + accepted.filter(a => a.file.type.startsWith('video/')).length >= PUBLIC_MAX_VIDEOS) {
          errors.push(`${file.name}: public gallery is limited to ${PUBLIC_MAX_VIDEOS} videos`);
          continue;
        }
      }

      // Video duration check
      if (isVid) {
        const maxSec = isPublic ? PUBLIC_MAX_VIDEO_SECONDS : PRIVATE_MAX_VIDEO_SECONDS;
        try {
          const duration = await getVideoDuration(file);
          if (duration > maxSec) {
            errors.push(`${file.name}: video is ${formatDuration(duration)} — ${isPublic ? `public videos must be ${PUBLIC_MAX_VIDEO_SECONDS} seconds` : `private videos must be under ${formatDuration(maxSec)}`} or less`);
            continue;
          }
        } catch {
          errors.push(`${file.name}: could not verify video duration`);
          continue;
        }
      }

      // File size check
      const sizeLimitMB = isVid ? 500 : 20;
      if (file.size > sizeLimitMB * 1024 * 1024) {
        errors.push(`${file.name}: exceeds ${sizeLimitMB}MB limit`);
        continue;
      }

      accepted.push({ id: `${Date.now()}-${Math.random()}`, file, uploading: false, done: false, error: null });
    }

    if (errors.length) setError(errors.join('\n'));
    if (accepted.length) setQueue(prev => [...prev, ...accepted]);
  };

  // ── Upload all queued ──────────────────────────────────────────────────

  const handleUploadAll = async (isPublic, publishAt) => {
    const queue = isPublic ? publicQueue : privateQueue;
    const setQueue = isPublic ? setPublicQueue : setPrivateQueue;
    const setUploading = isPublic ? setPublicUploading : setPrivateUploading;
    const setError = isPublic ? setPublicError : setPrivateError;

    if (queue.length === 0) return;
    setUploading(true);
    setError(null);
    setQueue(prev => prev.map(q => ({ ...q, uploading: true })));

    const results = await Promise.allSettled(
      queue.map(async (item) => {
        try {
          // Uploads default to is_public: true server-side; private items are
          // flipped with a follow-up PATCH.
          const created = await uploadSellerMedia(api, item.file, publishAt ? { publish_at: publishAt } : {});
          if (!isPublic) {
            await api.patch(`/api/v1/me/seller/media/${created.id}`, { is_public: false });
          }
          setQueue(prev => prev.map(q => q.id === item.id ? { ...q, uploading: false, done: true } : q));
        } catch (err) {
          setQueue(prev => prev.map(q => q.id === item.id ? { ...q, uploading: false, error: err.message || 'Upload failed' } : q));
          throw err;
        }
      })
    );

    if (results.some(r => r.status === 'rejected')) {
      setError('Some files failed to upload. Failed items are shown below.');
    }

    await refetch();
    setQueue(prev => prev.filter(q => !q.done));
    setUploading(false);
  };

  // ── Mutations ───────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    try {
      await api.del(`/api/v1/me/seller/media/${id}`);
      await refetch();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleSaveEdit = async (id, data) => {
    await api.patch(`/api/v1/me/seller/media/${id}`, data);
    await refetch();
  };

  const handleToggleVisibility = async (item) => {
    const makePublic = !item.is_public;
    if (makePublic) {
      // Respect public gallery capacity when promoting private items
      if (item.media_type === 'image' && publicPhotos.length >= PUBLIC_MAX_PHOTOS) {
        setPrivateError(`Public gallery is limited to ${PUBLIC_MAX_PHOTOS} photos — delete one first.`);
        return;
      }
      if (item.media_type === 'video' && publicVideos.length >= PUBLIC_MAX_VIDEOS) {
        setPrivateError(`Public gallery is limited to ${PUBLIC_MAX_VIDEOS} videos — delete one first.`);
        return;
      }
    }
    try {
      await api.patch(`/api/v1/me/seller/media/${item.id}`, { is_public: makePublic });
      await refetch();
    } catch (err) {
      const setError = item.is_public ? setPublicError : setPrivateError;
      setError(err.message || 'Failed to change visibility');
    }
  };

  const handlePublishNow = async (item) => {
    try {
      // Explicit null publishes immediately (contract §5 PATCH semantics)
      await api.patch(`/api/v1/me/seller/media/${item.id}`, { publish_at: null });
      await refetch();
    } catch (err) {
      const setError = item.is_public ? setPublicError : setPrivateError;
      setError(err.message || 'Failed to publish');
    }
  };

  const handleMove = async (mediaList, index, direction) => {
    const target = mediaList[index];
    const swap = mediaList[index + direction];
    if (!swap) return;
    await api.patch(`/api/v1/me/seller/media/${target.id}`, { sort_order: swap.sort_order });
    await api.patch(`/api/v1/me/seller/media/${swap.id}`, { sort_order: target.sort_order });
    await refetch();
  };

  if (loading) {
    return <Center py="xl"><Loader color="grape" /></Center>;
  }

  const sharedProps = (isPublic) => ({
    isPublic,
    media: isPublic ? publicMedia : privateMedia,
    queue: isPublic ? publicQueue : privateQueue,
    uploading: isPublic ? publicUploading : privateUploading,
    globalError: isPublic ? publicError : privateError,
    onFileSelect: (files) => handleFileSelect(files, isPublic),
    onUploadAll: (publishAt) => handleUploadAll(isPublic, publishAt),
    onClearQueue: () => (isPublic ? setPublicQueue : setPrivateQueue)([]),
    onRemoveFromQueue: (id) => (isPublic ? setPublicQueue : setPrivateQueue)(prev => prev.filter(q => q.id !== id)),
    onDelete: handleDelete,
    onEdit: setEditItem,
    onMoveUp: (index) => handleMove(isPublic ? publicMedia : privateMedia, index, -1),
    onMoveDown: (index) => handleMove(isPublic ? publicMedia : privateMedia, index, 1),
    onToggleVisibility: handleToggleVisibility,
    onPublishNow: handlePublishNow,
    editItem,
    onCloseEdit: () => setEditItem(null),
    onSaveEdit: handleSaveEdit,
  });

  return (
    <Stack gap="md" pt="md">
      {loadError && (
        <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
          Failed to load gallery: {loadError.message}
        </Alert>
      )}

      <Tabs defaultValue="public" color="grape">
        <Tabs.List>
          <Tabs.Tab value="public" leftSection={<IconEye size="0.9rem" />}>
            Public Gallery
            <Badge size="xs" color="blue" variant="light" ml={6}>
              {publicMedia.length}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="private" leftSection={<IconLock size="0.9rem" />}>
            Private Gallery
            <Badge size="xs" color="grape" variant="light" ml={6}>
              {privateMedia.length}
            </Badge>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="public" pt="lg">
          <UploadPanel {...sharedProps(true)} />
        </Tabs.Panel>

        <Tabs.Panel value="private" pt="lg">
          <UploadPanel {...sharedProps(false)} />
        </Tabs.Panel>
      </Tabs>

      {hasMore && (
        <Button
          variant="light"
          color="grape"
          onClick={handleLoadMore}
          loading={loadingMore}
          style={{ alignSelf: 'center' }}
        >
          Load more ({allMedia.length} of {mediaState.total})
        </Button>
      )}
    </Stack>
  );
};

export default SellerMediaGallery;
